import { CreateRequestLayout } from "@/components/customer/CreateRequestLayout";
import { ThemedText } from "@/components/themed-text";
import { BinaryChoice } from "@/components/ui/binary-choice";
import { CounterInput } from "@/components/ui/counter-input";
import { DateTimePickerInput } from "@/components/ui/date-time-picker";
import { BrandColors } from "@/constants/theme";
import { showAlert } from "@/lib/alert";
import {
  assetToRNFile,
  imagePickerPresets,
  normalizeImageAsset,
  pickImageFromCamera,
  pickImageFromLibrary,
  requestMediaLibraryPermission,
  validateFileSize,
} from "@/lib/image-picker";
import { BookingStep3Data, bookingStep3Schema } from "@/lib/validation/booking";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { router } from "expo-router";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { HelperText } from "react-native-paper";

export const VehicleCollection = ({ route = true }: { route?: boolean }) => {
  const { formData, setFormData, setCurrentStep } = useCreateRequestStore();
  const [isSchedule, setIsSchedule] = useState<boolean | null>(
    !formData.scheduled_at,
  );
  const scrollRef = useRef<ScrollView>(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<BookingStep3Data>({
    resolver: zodResolver(bookingStep3Schema),
    defaultValues: {
      vehicle_image: formData.vehicle_image || undefined,
      scheduled_at: formData.scheduled_at ?? null,
      passenger_count: formData.passenger_count ?? 0,
      pet_count: formData.pet_count ?? 0,
    },
  });

  const scheduledAt = watch("scheduled_at");
  const vehicleImage = watch("vehicle_image");
  const hasPassengers = watch("passenger_count") > 0;
  const serviceType = formData.service_type;

  const onSubmit = (data: BookingStep3Data) => {
    setFormData(data);
    setCurrentStep(4);
    if (route) {
      router.push("/customer/create-request/step4");
    }
  };

  const handleUploadImage = async (asset: ImagePicker.ImagePickerAsset) => {
    if (asset) {
      const normalizedAsset = await normalizeImageAsset(asset);
      const { isValid, message } = validateFileSize(normalizedAsset);
      if (!isValid) {
        setError("vehicle_image", {
          message: message,
        } as any);
        return;
      }

      setValue(
        "vehicle_image",
        assetToRNFile(normalizedAsset, "vehicle_image"),
      );
    }
  };

  const handlePickImage = async () => {
    if (vehicleImage) {
      return;
    }

    try {
      if (
        !(await requestMediaLibraryPermission(
          "We need access to your photo library to upload vehicle images.",
        ))
      ) {
        return;
      }

      clearErrors("vehicle_image");

      showAlert(
        "Select Image",
        "Choose an option",
        [
          {
            text: "Camera",
            onPress: async () => {
              const asset = await pickImageFromCamera(
                imagePickerPresets.vehicle,
                true,
                "We need access to your camera to take photos.",
              );
              if (asset) {
                await handleUploadImage(asset);
              }
            },
          },
          {
            text: "Photo Library",
            onPress: async () => {
              const asset = await pickImageFromLibrary(
                imagePickerPresets.vehicle,
              );
              if (asset) {
                await handleUploadImage(asset);
              }
            },
          },
          {
            text: "Cancel",
            style: "cancel",
          },
        ],
        { cancelable: true },
      );
    } catch (error) {
      console.error("Error picking image:", error);
    }
  };

  const isMobileService =
    serviceType === "mobile_mechanics" || serviceType === "mobile_valet";

  return (
    <CreateRequestLayout
      scrollRef={scrollRef}
      step={3}
      title="Vehicle Collection Information"
      subtitle="Please answer the following questions to help us provide you with the best possible service"
      onNext={handleSubmit(onSubmit)}
      onBack={() => (route ? router.back() : setCurrentStep(2))}
    >
      <View style={styles.section}>
        <ThemedText type="defaultSemiBold">
          Add vehicle photo (optional)
        </ThemedText>
        <ThemedText style={styles.photoHelper} type="small">
          Photos help providers give you the most accurate quotes.
        </ThemedText>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.photoRow}
        >
          {!vehicleImage ? (
            <TouchableOpacity
              style={styles.addPhotoCard}
              onPress={handlePickImage}
              activeOpacity={0.8}
            >
              <Ionicons
                name="images-outline"
                size={24}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.addPhotoTitle} type="smallSemiBold">
                Add Photo
              </ThemedText>
              <ThemedText style={styles.addPhotoSubtitle} type="extraSmall">
                Tap to upload
              </ThemedText>
            </TouchableOpacity>
          ) : (
            <View style={styles.photoThumbnail}>
              <Image
                source={{ uri: vehicleImage.uri }}
                style={styles.thumbnailImage}
              />
              <TouchableOpacity
                style={styles.removePhotoButton}
                onPress={() => setValue("vehicle_image", undefined as any)}
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <Ionicons name="close" size={14} color={BrandColors.white} />
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>

        {errors.vehicle_image && (
          <HelperText type="error">{errors.vehicle_image?.message}</HelperText>
        )}
      </View>

      <View style={styles.section}>
        <BinaryChoice
          label="When do you need this service?"
          value={isSchedule}
          options={{
            trueLabel: isMobileService ? "Request Now" : "Pick up now",
            trueDescription: isMobileService
              ? "Service as soon as possible"
              : "Pickup as soon as possible",
            falseLabel: isMobileService ? "Schedule Repairs" : "Schedule",
            falseDescription: "Choose a date and time",
            trueValue: true,
            falseValue: false,
          }}
          showIcon={false}
          onValueChange={(value) => {
            setIsSchedule(value);
            if (value) {
              setValue("scheduled_at", null);
            } else if (!scheduledAt) {
              setValue("scheduled_at", new Date());
            }
          }}
        />
        {isSchedule === false && (
          <DateTimePickerInput
            label="Schedule Date & time"
            value={scheduledAt ?? undefined}
            onChange={(date) => {
              setValue("scheduled_at", date);
            }}
            error={errors.scheduled_at?.message}
            showPickUpNowButton={false}
            minimumDate={new Date()}
          />
        )}
      </View>

      <View style={styles.section}>
        <Controller
          control={control}
          name="passenger_count"
          render={({ field: { value, onChange } }) => (
            <CounterInput
              label="Passenger count"
              value={value}
              onValueChange={(val) => {
                onChange(val);
                if (val <= 0) {
                  setValue("pet_count", 0);
                }
              }}
              min={0}
              max={50}
            />
          )}
        />
        <ThemedText style={styles.helperText} type="extraSmall">
          Enter the number of passengers that will be traveling
        </ThemedText>
        {errors.passenger_count && (
          <HelperText type="error">
            {errors.passenger_count?.message}
          </HelperText>
        )}
      </View>

      {hasPassengers && (
        <View
          style={styles.section}
          onLayout={() => {
            setTimeout(() => {
              scrollRef.current?.scrollToEnd({ animated: true });
            }, 50);
          }}
        >
          <Controller
            control={control}
            name="pet_count"
            render={({ field: { value, onChange } }) => (
              <CounterInput
                label="Pet count"
                value={value}
                onValueChange={onChange}
                min={0}
                max={10}
              />
            )}
          />
          <ThemedText style={styles.helperText} type="extraSmall">
            Enter the number of pets that will be traveling
          </ThemedText>
          {errors.pet_count && (
            <HelperText type="error">{errors.pet_count?.message}</HelperText>
          )}
        </View>
      )}
    </CreateRequestLayout>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 20,
    gap: 12,
  },
  photoHelper: {
    color: BrandColors.mediumGray,
    lineHeight: 18,
  },
  photoRow: {
    flexDirection: "row",
    gap: 12,
    paddingVertical: 4,
  },
  addPhotoCard: {
    width: 108,
    height: 108,
    borderRadius: 14,
    borderWidth: 1.5,
    borderStyle: "dashed",
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.primaryLight,
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    paddingHorizontal: 8,
  },
  addPhotoTitle: {
    color: BrandColors.primary,
    textAlign: "center",
  },
  addPhotoSubtitle: {
    color: BrandColors.mediumGray,
    textAlign: "center",
  },
  photoThumbnail: {
    width: 108,
    height: 108,
    borderRadius: 14,
    overflow: "hidden",
    position: "relative",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
  },
  removePhotoButton: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    alignItems: "center",
    justifyContent: "center",
  },
  helperText: {
    fontSize: 12,
    marginTop: 8,
  },
});
